import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArchiveIcon,
  CopyIcon,
  DownloadIcon,
  FileIcon,
  FilePlusIcon,
  LockIcon,
  LockOpenIcon,
  PauseIcon,
  PlayIcon,
  Trash2Icon,
  UploadIcon,
} from "lucide-react";
import { useMemo, useRef, useState } from "react";

import {
  downloadOperatorsSkillExport,
  operatorsSkillMutationOptions,
  type OperatorsSkillMutation,
} from "../../lib/operatorsSkillsReactQuery";
import { cn } from "../../lib/utils";
import type { OperatorsSkillDetail } from "../../operators";
import ChatMarkdown from "../ChatMarkdown";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { Textarea } from "../ui/textarea";
import { toastManager } from "../ui/toast";
import { AddSkillFileDialog, DuplicateSkillDialog } from "./SkillDialogs";
import { SkillBadges } from "./SkillBadges";
import { findSkillMarkdownProblem } from "./skillMarkdown";

const MANIFEST_PATH = "SKILL.md";

/** Pending edits: a string rewrites the file, null deletes it. */
type Edits = Record<string, string | null>;

export function SkillEditor({ skill }: { skill: OperatorsSkillDetail }) {
  const queryClient = useQueryClient();
  const mutation = useMutation(operatorsSkillMutationOptions(queryClient));
  const replaceInput = useRef<HTMLInputElement>(null);
  const [edits, setEdits] = useState<Edits>({});
  const [selectedPath, setSelectedPath] = useState(MANIFEST_PATH);
  const [preview, setPreview] = useState(false);
  const [dialog, setDialog] = useState<"add-file" | "archive" | "duplicate" | null>(null);
  const editable = skill.canEdit;

  const files = useMemo(() => {
    const byPath = new Map(skill.files.map((file) => [file.path, file.text]));
    for (const [path, text] of Object.entries(edits)) {
      if (text === null) {
        byPath.delete(path);
      } else {
        byPath.set(path, text);
      }
    }
    return [...byPath.entries()]
      .map(([path, text]) => ({ path, text }))
      .sort((left, right) => left.path.localeCompare(right.path));
  }, [edits, skill.files]);
  const selected = files.find((file) => file.path === selectedPath) ?? files[0];
  const dirty = Object.keys(edits).length > 0;
  const manifest = files.find((file) => file.path === MANIFEST_PATH)?.text ?? "";
  const problem = editable ? findSkillMarkdownProblem(manifest, skill.name) : null;

  const run = async (variables: OperatorsSkillMutation, success?: string) => {
    try {
      await mutation.mutateAsync(variables);
      if (variables.kind === "save" || variables.kind === "replace") {
        setEdits({});
      }
      if (success) toastManager.add({ title: success, type: "success" });
    } catch (error) {
      toastManager.add({
        description: error instanceof Error ? error.message : "Request failed.",
        title: "Skill update failed",
        type: "error",
      });
    }
  };

  const exportSkill = async () => {
    try {
      await downloadOperatorsSkillExport(skill);
    } catch (error) {
      toastManager.add({
        description: error instanceof Error ? error.message : "Could not export skill.",
        title: "Export failed",
        type: "error",
      });
    }
  };

  const addFile = (path: string) => {
    setEdits((current) => ({ ...current, [path]: "" }));
    setSelectedPath(path);
    setPreview(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1 basis-72 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="break-words font-mono text-xl font-semibold">{skill.name}</h1>
            <SkillBadges skill={skill} />
          </div>
          <p className="max-w-3xl text-sm text-muted-foreground">{skill.description}</p>
          <p className="text-xs text-muted-foreground">
            {skill.ownerEmail
              ? `Owned by ${skill.ownerEmail}`
              : skill.builtin
                ? "Maintained by the repository"
                : "Shared with the workspace"}
          </p>
        </div>
        <div className="flex flex-wrap gap-2" aria-label="Skill actions">
          <Button size="xs" variant="outline" onClick={() => setDialog("duplicate")}>
            <CopyIcon /> Duplicate
          </Button>
          <Button size="xs" variant="outline" onClick={() => void exportSkill()}>
            <DownloadIcon /> Export
          </Button>
          {editable ? (
            <>
              <Button size="xs" variant="outline" onClick={() => replaceInput.current?.click()}>
                <UploadIcon /> Replace files
              </Button>
              <input
                ref={replaceInput}
                aria-label="Replace skill from zip"
                hidden
                type="file"
                accept=".zip,application/zip"
                onChange={(event) => {
                  const zip = event.target.files?.[0];
                  event.target.value = "";
                  if (zip) void run({ id: skill.id, kind: "replace", zip }, "Skill files replaced");
                }}
              />
            </>
          ) : null}
        </div>
      </div>

      {skill.builtin ? (
        <Card className="p-4 text-sm text-muted-foreground">
          Built-in skills are maintained in the Operators repository and can't be edited here.
          Duplicate this skill to customize it.
        </Card>
      ) : null}
      {skill.locked && !editable && !skill.builtin ? (
        <Card className="p-4 text-sm text-muted-foreground">
          Locked: only {skill.ownerEmail ? `${skill.ownerEmail} and admins` : "admins"} can edit
          this skill. Duplicate it to make your own version.
        </Card>
      ) : null}
      {skill.status === "disabled" ? (
        <Card className="p-4 text-sm text-muted-foreground">
          Disabled skills do not reach new threads. Existing threads keep their current copy.
        </Card>
      ) : null}
      {skill.status === "archived" ? (
        <Card className="p-4 text-sm text-muted-foreground">
          Archived skills no longer reach new threads and are read-only.
        </Card>
      ) : null}

      {skill.canManage && skill.status !== "archived" ? (
        <Card className="gap-3 p-4">
          <div>
            <h2 className="text-sm font-medium">Availability and access</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              These changes affect who can edit this skill and whether it reaches new threads.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              size="xs"
              variant="outline"
              disabled={mutation.isPending}
              onClick={() =>
                void run(
                  { id: skill.id, kind: "update", locked: !skill.locked },
                  skill.locked ? "Anyone can edit this skill" : "Skill editing locked",
                )
              }
            >
              {skill.locked ? <LockOpenIcon /> : <LockIcon />}
              {skill.locked ? "Unlock edits" : "Lock edits"}
            </Button>
            <Button
              size="xs"
              variant="outline"
              disabled={mutation.isPending}
              onClick={() =>
                void run(
                  {
                    id: skill.id,
                    kind: "update",
                    status: skill.status === "active" ? "disabled" : "active",
                  },
                  skill.status === "active" ? "Skill disabled" : "Skill enabled",
                )
              }
            >
              {skill.status === "active" ? <PauseIcon /> : <PlayIcon />}
              {skill.status === "active" ? "Disable" : "Enable"}
            </Button>
            <Button
              size="xs"
              variant="ghost"
              disabled={mutation.isPending}
              onClick={() => setDialog("archive")}
            >
              <ArchiveIcon /> Archive
            </Button>
          </div>
        </Card>
      ) : null}

      <section className="@container/files space-y-2" aria-labelledby="skill-files-heading">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <div>
            <h2 id="skill-files-heading" className="text-sm font-medium">
              Files
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">
              SKILL.md tells Codex when and how to use this skill.
            </p>
          </div>
          {editable ? (
            <Button size="xs" variant="outline" onClick={() => setDialog("add-file")}>
              <FilePlusIcon /> Add file
            </Button>
          ) : null}
        </div>
        <div className="grid gap-4 @2xl/files:grid-cols-[14rem_minmax(0,1fr)]">
          <Card className="min-w-0 gap-1 p-2">
            {files.map((file) => (
              <button
                key={file.path}
                type="button"
                aria-current={file.path === selected?.path ? "page" : undefined}
                className={cn(
                  "flex min-w-0 items-center gap-2 rounded-md px-2 py-1.5 text-left font-mono text-xs outline-none hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring",
                  file.path === selected?.path && "bg-accent",
                )}
                onClick={() => {
                  setSelectedPath(file.path);
                  setPreview(false);
                }}
              >
                <FileIcon className="size-3.5 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate">{file.path}</span>
                {file.path in edits ? (
                  <span className="text-warning" aria-label="Unsaved changes">
                    •
                  </span>
                ) : null}
              </button>
            ))}
          </Card>

          <Card className="min-w-0 gap-3 p-4">
            {selected ? (
              <>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="min-w-0 break-all font-mono text-xs text-muted-foreground">
                    {selected.path}
                  </span>
                  <div className="flex shrink-0 gap-2">
                    {selected.path.endsWith(".md") && selected.text !== null ? (
                      <Button size="xs" variant="outline" onClick={() => setPreview(!preview)}>
                        {preview ? "Edit" : "Preview"}
                      </Button>
                    ) : null}
                    {editable && selected.path !== MANIFEST_PATH ? (
                      <Button
                        size="xs"
                        variant="ghost"
                        onClick={() => {
                          setEdits((current) => ({ ...current, [selected.path]: null }));
                          setPreview(false);
                        }}
                      >
                        <Trash2Icon /> Delete
                      </Button>
                    ) : null}
                  </div>
                </div>
                {selected.text === null ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">
                    Binary file. Export the skill to view it, or replace the skill from a zip.
                  </p>
                ) : preview ? (
                  <div className="prose-sm max-w-none overflow-x-auto">
                    <ChatMarkdown text={selected.text} cwd={undefined} />
                  </div>
                ) : (
                  <Textarea
                    aria-label={`Contents of ${selected.path}`}
                    className="min-h-96 resize-y font-mono text-xs"
                    readOnly={!editable}
                    value={selected.text}
                    onChange={(event) =>
                      setEdits((current) => ({
                        ...current,
                        [selected.path]: event.target.value,
                      }))
                    }
                  />
                )}
              </>
            ) : (
              <p className="py-8 text-center text-sm text-muted-foreground">
                This skill has no files.
              </p>
            )}
            {editable ? (
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3">
                <p
                  role={problem ? "alert" : undefined}
                  className={cn("text-xs", problem ? "text-destructive" : "text-muted-foreground")}
                >
                  {problem ?? "Saving updates the skill for everyone's new threads."}
                </p>
                <div className="ml-auto flex gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={!dirty || mutation.isPending}
                    onClick={() => setEdits({})}
                  >
                    Discard
                  </Button>
                  <Button
                    size="sm"
                    disabled={!dirty || problem !== null || mutation.isPending}
                    onClick={() => void run({ edits, id: skill.id, kind: "save" }, "Skill saved")}
                  >
                    {mutation.isPending && mutation.variables?.kind === "save"
                      ? "Saving…"
                      : "Save changes"}
                  </Button>
                </div>
              </div>
            ) : null}
          </Card>
        </div>
      </section>

      <section className="space-y-2" aria-labelledby="skill-versions-heading">
        <h2 id="skill-versions-heading" className="text-sm font-medium">
          Version history
        </h2>
        <Card className="gap-0 divide-y divide-border p-0">
          {skill.versions.map((version) => (
            <div key={version.id} className="flex flex-wrap items-center gap-3 px-4 py-2 text-sm">
              <span className="font-mono">v{version.version}</span>
              <span className="break-all text-muted-foreground">
                {version.createdByEmail ?? "repository"}
              </span>
              <span className="ml-auto text-xs text-muted-foreground">
                {new Date(version.createdAt).toLocaleString()}
              </span>
            </div>
          ))}
        </Card>
      </section>

      {dialog === "duplicate" ? (
        <DuplicateSkillDialog skill={skill} onClose={() => setDialog(null)} />
      ) : null}
      {dialog === "add-file" ? (
        <AddSkillFileDialog
          existingPaths={files.map((file) => file.path)}
          onAdd={addFile}
          onClose={() => setDialog(null)}
        />
      ) : null}
      <AlertDialog open={dialog === "archive"} onOpenChange={(open) => !open && setDialog(null)}>
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive {skill.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This skill will stop reaching new threads and become read-only. It will remain
              available from the archived filter.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" disabled={mutation.isPending} />}>
              Cancel
            </AlertDialogClose>
            <AlertDialogClose
              render={<Button variant="destructive" disabled={mutation.isPending} />}
              onClick={() =>
                void run({ id: skill.id, kind: "update", status: "archived" }, "Skill archived")
              }
            >
              Archive skill
            </AlertDialogClose>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </div>
  );
}
